import {
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useBackTarget } from '../../pricingJourney/navigation';
import { useQuery } from '@tanstack/react-query';
import PageSkeleton from '../../pricingJourney/components/Skeletons';

import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import ArrowBackRoundedIcon
  from '@mui/icons-material/ArrowBackRounded';

import MarginCenterEntityTrendPanel
  from '../components/MarginCenterEntityTrendPanel';

import CommercialQueryPanel
  from '../components/CommercialQueryPanel';

import ProductCustomersDecisionPanel
  from '../components/ProductCustomersDecisionPanel';

import ProductEconomicStudyPanel
  from '../components/ProductEconomicStudyPanel';

import ProductPositionPanel
  from '../components/ProductPositionPanel';

import ProductKpiBreakdownModal, {
  type ProductKpiBreakdownMetric,
} from '../components/ProductKpiBreakdownModal';

import {
  getMarginCenterCapabilities,
  getMarginCenterProductContext,
} from '../services/marginCenterService';

import {
  MarginCenterContextMetricCard,
  MarginCenterEntityContextHeader,
} from '../components/MarginCenterEntityContextHeader';

import type {
  MarginCenterCommercialQueryContext,
  MarginCenterComparisonMode,
  MarginCenterProductContextParams,
  MarginCenterTimeGrain,
} from '../types/marginCenter';


const COMPARISON_MODES: MarginCenterComparisonMode[] = [
  'previous_period',
  'year_over_year',
  'custom',
  'none',
];

const TIME_GRAINS: MarginCenterTimeGrain[] = [
  'day',
  'week',
  'month',
];


function readComparisonMode(
  value: string | null,
): MarginCenterComparisonMode {
  if (
    value
    && COMPARISON_MODES.includes(
      value as MarginCenterComparisonMode,
    )
  ) {
    return value as MarginCenterComparisonMode;
  }

  return 'previous_period';
}


function readTimeGrain(
  value: string | null,
): MarginCenterTimeGrain {
  if (
    value
    && TIME_GRAINS.includes(
      value as MarginCenterTimeGrain,
    )
  ) {
    return value as MarginCenterTimeGrain;
  }

  return 'week';
}


const numberFormatter = new Intl.NumberFormat(
  'es-ES',
  {
    maximumFractionDigits: 2,
  },
);

const integerFormatter = new Intl.NumberFormat(
  'es-ES',
  {
    maximumFractionDigits: 0,
  },
);


function formatNumber(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return numberFormatter.format(value);
}


function formatInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return integerFormatter.format(value);
}


function formatPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${numberFormatter.format(value)} %`;
}


function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (!currencyCode) {
    return formatNumber(value);
  }

  try {
    return new Intl.NumberFormat(
      'es-ES',
      {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      },
    ).format(value);
  } catch {
    return formatNumber(value);
  }
}


function formatSignedPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${numberFormatter.format(value)} %`;
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${numberFormatter.format(value)} pp`;
}


function formatSignedInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${integerFormatter.format(value)}`;
}

export default function MarginCenterProductPage() {
  const navigate = useNavigate();
  // PRC-027: «Volver» al origen real (Overview, Explorer, Optimización, Control comercial); sin origen, Explorer.
  const back = useBackTarget('/margin-center/explorer', 'Volver al Explorer');

  const { productId } =
    useParams<{ productId: string }>();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const [
    kpiDetail,
    setKpiDetail,
  ] = useState<ProductKpiBreakdownMetric | null>(
    null,
  );

  const queryParams =
    useMemo<MarginCenterProductContextParams>(
      () => {
        const comparisonMode =
          readComparisonMode(
            searchParams.get('comparison'),
          );

        return {
          start_date:
            searchParams.get('from')
            || undefined,

          end_date:
            searchParams.get('to')
            || undefined,

          comparison_mode:
            comparisonMode,

          comparison_start_date:
            comparisonMode === 'custom'
              ? (
                searchParams.get(
                  'comparisonFrom',
                )
                || undefined
              )
              : undefined,

          comparison_end_date:
            comparisonMode === 'custom'
              ? (
                searchParams.get(
                  'comparisonTo',
                )
                || undefined
              )
              : undefined,

          time_grain:
            readTimeGrain(
              searchParams.get('grain'),
            ),
        };
      },
      [searchParams],
    );

  const capabilitiesQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'capabilities',
      ],
      queryFn:
        getMarginCenterCapabilities,
    });

  const productQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'product',
        productId,
        queryParams,
      ],
      queryFn: () =>
        getMarginCenterProductContext(
          productId ?? '',
          queryParams,
        ),
      enabled: Boolean(productId),
      placeholderData:
        (previousData) =>
          previousData,
    });

  const context =
    productQuery.data;

  const currencyCode =
    capabilitiesQuery.data
      ?.presentation
      .currency_code
    ?? null;

  const commercialQueryContext =
    useMemo<
      MarginCenterCommercialQueryContext | null
    >(
      () => {
        if (
          !context
          || context.status !== 'ok'
          || !context.product
        ) {
          return null;
        }

        return {
          start_date:
            context.selection.start_date
            ?? undefined,

          end_date:
            context.selection.end_date
            ?? undefined,

          comparison_mode:
            context.selection.comparison_mode,

          comparison_start_date:
            context.selection.comparison_mode
              === 'custom'
              ? (
                context.selection
                  .comparison_start_date
                ?? undefined
              )
              : undefined,

          comparison_end_date:
            context.selection.comparison_mode
              === 'custom'
              ? (
                context.selection
                  .comparison_end_date
                ?? undefined
              )
              : undefined,

          time_grain:
            context.selection.time_grain,

          product_id:
            context.product.product_id,
        };
      },
      [context],
    );

  const goBackToExplorer = () => {
    if (back.fromOrigin) {
      back.go();
      return;
    }

    const next =
      new URLSearchParams(
        searchParams,
      );

    next.delete('returnTo');
    next.delete('product');
    next.delete('customer');

    next.delete(
      'relationshipCustomer',
    );

    next.delete(
      'relationshipProduct',
    );

    next.delete(
      'productCustomerSearch',
    );

    next.delete(
      'productCustomerSort',
    );

    next.delete(
      'productCustomerDirection',
    );

    next.delete(
      'productCustomerCursor',
    );

    const query =
      next.toString();

    navigate(
      `/margin-center/explorer${
        query
          ? `?${query}`
          : ''
      }`,
    );
  };

  // PRC-031: forma de la ficha mientras carga, sin saltos.
  if (productQuery.isLoading) {
    return (
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: 3 }}>
        <PageSkeleton variant="detail" bare padded={false} />
      </Box>
    );
  }

  if (productQuery.isError) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={goBackToExplorer}
          sx={{ mb: 2 }}
        >
          {back.label}
        </Button>

        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                productQuery.refetch()
              }
            >
              Reintentar
            </Button>
          }
        >
          No se pudo cargar el contexto del producto.
        </Alert>
      </Box>
    );
  }

  if (!context) {
    return null;
  }

  if (
    context.status === 'empty_dataset'
  ) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Button
          startIcon={
            <ArrowBackRoundedIcon />
          }
          onClick={
            goBackToExplorer
          }
          sx={{ mb: 2 }}
        >
          {back.label}
        </Button>

        <Alert severity="info">
          Esta cuenta todavía no dispone de actividad
          transaccional.
        </Alert>
      </Box>
    );
  }

  if (
    context.status
      === 'product_not_found'
    || !context.product
  ) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Button
          startIcon={
            <ArrowBackRoundedIcon />
          }
          onClick={
            goBackToExplorer
          }
          sx={{ mb: 2 }}
        >
          {back.label}
        </Button>

        <Alert severity="warning">
          No se ha encontrado este producto en la
          cuenta actual.
        </Alert>
      </Box>
    );
  }

  const {
    product,
    current,
    comparison,
    changes,
    coverage,
    selection,
  } = context;

  const visibleAttributes = [
    product.attributes.product_category,
    product.attributes.product_brand,
    product.attributes.unit_of_measure,
  ].filter(Boolean) as string[];

  const openRelationship = (
    customerId: string,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    next.delete(
      'relationshipProduct',
    );

    next.set(
      'relationshipCustomer',
      customerId,
    );

    setSearchParams(next);
  };

  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, md: 2 },
        py: 3,
      }}
    >
      <Stack spacing={2.5}>
        <MarginCenterEntityContextHeader
          entityType="Producto"
          label={product.label}
          entityId={product.product_id}
          attributes={visibleAttributes}
          selection={selection}
          dataset={context.dataset}
          onBack={goBackToExplorer}
          backLabel={back.label}
        />

        {/* DUX-03D: acceso contextual; el producto se conserva seleccionado. */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#0f172a', border: '1px solid rgba(56,189,248,0.22)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 800 }}>Control comercial</Typography>
              <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 800 }}>Investigaciones de este producto</Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>Casos propios y vinculados a sus clientes, abiertos y cerrados, sin duplicar expedientes.</Typography>
            </Box>
            <Button variant="contained" sx={{ textTransform: 'none', flexShrink: 0 }} onClick={() => {
              const next = new URLSearchParams({
                context: 'product',
                productId: product.product_id,
                returnTo: `/margin-center/products/${encodeURIComponent(product.product_id)}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
              });
              navigate(`/margin-center/control-comercial?${next.toString()}`);
            }}>Ver seguimientos del producto</Button>
          </Stack>
        </Paper>

        {context.status === 'no_results' && (
          <Alert severity="info">
            El producto existe, pero no tiene actividad
            en los periodos seleccionados.
          </Alert>
        )}

        {current && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm:
                  'repeat(2, minmax(0, 1fr))',
                lg:
                  'repeat(5, minmax(0, 1fr))',
              },
              gap: 1.25,
            }}
          >
            <MarginCenterContextMetricCard
              label="Ingresos"
              value={formatCurrency(
                current.revenue,
                currencyCode,
              )}
              change={
                changes
                  ? `${
                      formatSignedPercentage(
                        changes.revenue_delta_pct,
                      )
                    } vs comparación`
                  : undefined
              }
              changeValue={
                changes?.revenue_delta_pct
              }
              comparison={
                comparison
                  ? formatCurrency(
                      comparison.revenue,
                      currencyCode,
                    )
                  : undefined
              }
              onClick={() =>
                setKpiDetail('revenue')
              }
            />

            <MarginCenterContextMetricCard
              label={
                currencyCode === 'EUR'
                  ? 'Margen €'
                  : currencyCode
                    ? `Margen ${currencyCode}`
                    : 'Margen'
              }
              value={formatCurrency(
                current.margin_amount,
                currencyCode,
              )}
              change={
                changes
                  ?.margin_amount_delta_pct
                  !== null
                && changes
                  ?.margin_amount_delta_pct
                  !== undefined
                  ? `${
                      formatSignedPercentage(
                        changes
                          .margin_amount_delta_pct,
                      )
                    } vs comparación`
                  : undefined
              }
              changeValue={
                changes
                  ?.margin_amount_delta_pct
              }
              comparison={
                comparison
                  ? formatCurrency(
                      comparison.margin_amount,
                      currencyCode,
                    )
                  : undefined
              }
              onClick={() =>
                setKpiDetail('margin_amount')
              }
            />

            <MarginCenterContextMetricCard
              label="Margen %"
              value={formatPercentage(
                current.margin_percentage,
              )}
              change={
                changes
                  ?.margin_percentage_delta_points
                  !== null
                && changes
                  ?.margin_percentage_delta_points
                  !== undefined
                  ? `${
                      formatSignedPoints(
                        changes
                          .margin_percentage_delta_points,
                      )
                    } vs comparación`
                  : undefined
              }
              changeValue={
                changes
                  ?.margin_percentage_delta_points
              }
              comparison={
                comparison
                  ? formatPercentage(
                      comparison
                        .margin_percentage,
                    )
                  : undefined
              }
              onClick={() =>
                setKpiDetail('margin')
              }
            />

            <MarginCenterContextMetricCard
              label="Clientes activos"
              value={formatInteger(
                current.active_customers,
              )}
              change={
                changes
                  ? `${
                      formatSignedInteger(
                        changes
                          .active_customers_delta,
                      )
                    } vs comparación`
                  : undefined
              }
              changeValue={
                changes
                  ?.active_customers_delta
              }
              comparison={
                comparison
                  ? formatInteger(
                      comparison
                        .active_customers,
                    )
                  : undefined
              }
              onClick={() =>
                setKpiDetail(
                  'active_customers',
                )
              }
            />

            <MarginCenterContextMetricCard
              label="Unidades"
              value={formatNumber(
                current.units,
              )}
              change={
                changes
                  ? `${
                      formatSignedPercentage(
                        changes.units_delta_pct,
                      )
                    } vs comparación`
                  : undefined
              }
              changeValue={
                changes?.units_delta_pct
              }
              comparison={
                comparison
                  ? formatNumber(
                      comparison.units,
                    )
                  : undefined
              }
              onClick={() =>
                setKpiDetail('units')
              }
            />
          </Box>
        )}

        <ProductKpiBreakdownModal
          open={kpiDetail !== null}
          metric={
            kpiDetail ?? 'revenue'
          }
          productId={
            product.product_id
          }
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          currencyCode={currencyCode}
          onClose={() =>
            setKpiDetail(null)
          }
          onOpenRelationship={
            openRelationship
          }
        />

        <ProductEconomicStudyPanel
          productId={product.product_id}
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          currencyCode={currencyCode}
        />

        {coverage
          && coverage.current
            .cost_coverage_pct < 100
          && (
          <Alert severity="info">
            La cobertura de coste de esta selección es
            del{' '}
            {formatPercentage(
              coverage.current
                .cost_coverage_pct,
            )}
            . Las métricas dependientes de coste mantienen
            las filas no resueltas fuera del cálculo
            financiero.
          </Alert>
        )}

        {context.status === 'ok' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg:
                  'minmax(0, 2.15fr) minmax(320px, 1fr)',
              },
              gap: 1.5,
              alignItems: 'stretch',
            }}
          >
            <MarginCenterEntityTrendPanel
              series={context.series}
              selection={selection}
              currencyCode={currencyCode}
            />

            <CommercialQueryPanel
              context={commercialQueryContext}
              currencyCode={currencyCode}
              hasPartialBucket={
                context.series.some(
                  (point) =>
                    point.is_partial_bucket,
                )
              }
              scopeLabel={
                `Producto · ${product.label}`
              }
              questionTitle="Pregunta sobre este producto"
              suggestedQuestions={[
                '¿Qué clientes tienen mayor caída de ingresos?',
                '¿Dónde sube el precio y bajan las unidades?',
                '¿Qué clientes pierden margen %?',
                '¿Dónde sube el coste unitario y cae el margen %?',
              ]}
              onOpenCustomer={() => undefined}
              onOpenRelationship={(
                customerId,
                _productId,
              ) =>
                openRelationship(customerId)
              }
            />
          </Box>
        )}

        <ProductPositionPanel
          productId={product.product_id}
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          currencyCode={currencyCode}
        />

        <ProductCustomersDecisionPanel
          productId={product.product_id}
          startDate={
            queryParams.start_date
            ?? selection.start_date
          }
          endDate={
            queryParams.end_date
            ?? selection.end_date
          }
          comparisonMode={
            queryParams.comparison_mode
            ?? selection.comparison_mode
          }
          comparisonStartDate={
            queryParams.comparison_start_date
            ?? selection.comparison_start_date
          }
          comparisonEndDate={
            queryParams.comparison_end_date
            ?? selection.comparison_end_date
          }
          timeGrain={
            queryParams.time_grain
            ?? selection.time_grain
          }
          currencyCode={currencyCode}
        />
      </Stack>
    </Box>
  );
}